<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs">
      <template #actions>
        <router-link :to="`/app/exams/${examId}/test-series`" class="btn btn-outline-secondary btn-sm">
          <vue-feather type="arrow-left" size="14" class="me-1" />
          Back
        </router-link>
      </template>
    </breadcrumb-index>

    <div class="card">
      <div class="card-header">
        <h6 class="card-title mb-0">Add Test Series</h6>
      </div>
      <div class="card-body">
        <form @submit.prevent="handleSubmit">
          <div class="row">
            <div class="col-lg-8">
              <div class="mb-3">
                <label class="form-label">Series Name <span class="text-danger">*</span></label>
                <input type="text" class="form-control" v-model="form.name" required placeholder="e.g., UPSC Prelims Mock Test Series 2025" />
              </div>

              <div class="mb-3">
                <label class="form-label">Description</label>
                <textarea class="form-control" v-model="form.description" rows="3" placeholder="Describe this test series..."></textarea>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Total Tests</label>
                    <input type="number" class="form-control" v-model="form.totalTests" min="0" />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Status</label>
                    <select class="form-select" v-model="form.status">
                      <option value="draft">Draft</option>
                      <option value="active">Active</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <div class="form-check">
                  <input type="checkbox" class="form-check-input" id="isFree" v-model="form.isFree" />
                  <label class="form-check-label" for="isFree">This is a free test series</label>
                </div>
              </div>

              <div class="mb-3" v-if="!form.isFree">
                <label class="form-label">Price (₹)</label>
                <input type="number" class="form-control" v-model="form.price" min="0" step="0.01" />
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Start Date</label>
                    <input type="date" class="form-control" v-model="form.startDate" />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">End Date</label>
                    <input type="date" class="form-control" v-model="form.endDate" />
                  </div>
                </div>
              </div>

              <div class="d-flex gap-2 pt-2">
                <button type="submit" class="btn btn-primary" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
                  Create Test Series
                </button>
                <router-link :to="`/app/exams/${examId}/test-series`" class="btn btn-outline-secondary">Cancel</router-link>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/services/api'
import { useAlert } from '@/plugins/sweetalert'

export default {
  name: 'AddTestSeries',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const alert = useAlert()
    const examId = route.params.examId

    const loading = ref(false)
    const exam = ref(null)
    const form = ref({
      name: '',
      description: '',
      totalTests: 0,
      price: 0,
      isFree: true,
      status: 'draft',
      startDate: '',
      endDate: ''
    })

    const breadcrumbs = computed(() => [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Exams', url: '/app/exams/list' },
      { title: exam.value?.name || 'Exam', url: `/app/exams/${examId}/test-series` },
      { title: 'Add Test Series', active: true }
    ])

    const fetchExam = async () => {
      try {
        const res = await api.get(`/v1/admin/exams/${examId}`)
        exam.value = res.data.exam
      } catch (err) {
        console.error('Failed to fetch exam:', err)
      }
    }

    const handleSubmit = async () => {
      loading.value = true
      try {
        await api.post(`/v1/admin/testseries`, {
          testSeries: {
            ...form.value,
            examId
          }
        })
        alert.success('Success', 'Test series created successfully')
        router.push(`/app/exams/${examId}/test-series`)
      } catch (err) {
        alert.error('Error', err.response?.data?.message || 'Failed to create test series')
      } finally {
        loading.value = false
      }
    }

    onMounted(fetchExam)

    return { examId, form, loading, breadcrumbs, handleSubmit }
  }
}
</script>
